import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field, ValidationSummary, FieldNote } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useAors, useCreateAor, useUpdateAor, useDeleteAor } from "@/hooks/use-aors";
import type { Aor, AorCreate } from "@/types";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

/** Defaults applied when a numeric box is left empty ("unset"). */
const AOR_DEFAULTS = {
  max_contacts: 1,
  minimum_expiration: 60,
  maximum_expiration: 7200,
  qualify_frequency: 0,
  qualify_timeout: 3.0,
};

/** Empty string means "unset — fall back to the default on save". */
type NumInput = number | "";

interface AorForm {
  name: string;
  max_contacts: NumInput;
  minimum_expiration: NumInput;
  maximum_expiration: NumInput;
  qualify_frequency: NumInput;
  qualify_timeout: NumInput;
  qualify_2xx_only: boolean;
  authenticate_qualify: boolean;
  remove_existing: boolean;
  remove_unavailable: boolean;
  support_path: boolean;
  contact: string[];
  outbound_proxy: string | null;
  mailboxes: string | null;
}

const defaultAor: AorForm = {
  name: "",
  ...AOR_DEFAULTS,
  qualify_2xx_only: false,
  authenticate_qualify: false,
  remove_existing: false,
  remove_unavailable: false,
  support_path: false,
  contact: [],
  outbound_proxy: null,
  mailboxes: null,
};

/** contact is comma-joined into a single VARCHAR(255) column server-side. */
const CONTACT_MAX_CHARS = 255;
const NAME_MAX_CHARS = 80;
/** "[" / "]" open a section, ";" starts a comment, newlines split lines. */
const ILLEGAL_NAME_CHARS = /[[\];\r\n]/;

const numOrEmpty = (raw: string): NumInput => (raw.trim() === "" ? "" : Number(raw));
const withDefault = (v: NumInput, fallback: number) => (v === "" ? fallback : v);
const joinedContacts = (contact: string[]) =>
  contact.map((c) => c.trim()).filter(Boolean).join(",");

const columns: Column<Aor>[] = [
  { key: "name", header: "Name" },
  { key: "max_contacts", header: "Max Contacts" },
  {
    key: "contact",
    header: "Contact",
    render: (a) => (
      <span className="text-xs text-muted-foreground">
        {a.contact?.length ? a.contact.join(", ") : "-"}
      </span>
    ),
  },
  { key: "maximum_expiration", header: "Max Expiry" },
  { key: "qualify_frequency", header: "Qualify Freq" },
];

export default function AorsPage() {
  const { data, isLoading, error, refetch, isFetching } = useAors();
  const createMut = useCreateAor();
  const updateMut = useUpdateAor();
  const deleteMut = useDeleteAor();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Aor | null>(null);
  const [form, setForm] = useState<AorForm>(defaultAor);
  const [showErrors, setShowErrors] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Aor | null>(null);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => [a.name, a.contact?.join(", ")].some((v) => v?.toLowerCase().includes(q)));
  }, [list, query]);

  const openCreate = () => { setEditing(null); setForm(defaultAor); setShowErrors(false); setOpen(true); };
  const openEdit = (a: Aor) => {
    setEditing(a);
    setForm({
      name: a.name,
      max_contacts: a.max_contacts,
      minimum_expiration: a.minimum_expiration,
      maximum_expiration: a.maximum_expiration,
      qualify_frequency: a.qualify_frequency,
      qualify_timeout: a.qualify_timeout,
      qualify_2xx_only: a.qualify_2xx_only,
      authenticate_qualify: a.authenticate_qualify,
      remove_existing: a.remove_existing,
      remove_unavailable: a.remove_unavailable,
      support_path: a.support_path,
      contact: a.contact ?? [],
      outbound_proxy: a.outbound_proxy,
      mailboxes: a.mailboxes,
    });
    setShowErrors(true);
    setOpen(true);
  };

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    const contactErrors: Record<number, string> = {};

    const name = form.name.trim();
    if (!name) e.name = "Name is required.";
    else if (ILLEGAL_NAME_CHARS.test(name)) e.name = "Name cannot contain [ ] ; or line breaks.";
    else if (name.length > NAME_MAX_CHARS) e.name = `Name must be ${NAME_MAX_CHARS} characters or fewer.`;
    else if (list.some((a) => a.name === name && a.id !== editing?.id)) e.name = "An AOR with this name already exists.";

    const nonNegative: [keyof AorForm, string][] = [
      ["max_contacts", "Max Contacts"],
      ["minimum_expiration", "Minimum Expiration"],
      ["maximum_expiration", "Maximum Expiration"],
      ["qualify_frequency", "Qualify Frequency"],
      ["qualify_timeout", "Qualify Timeout"],
    ];
    for (const [key, label] of nonNegative) {
      const raw = form[key] as NumInput;
      if (raw === "") continue;
      if (!Number.isFinite(raw)) e[key] = `${label} must be a number.`;
      else if (raw < 0) e[key] = `${label} cannot be negative.`;
    }

    const minExp = withDefault(form.minimum_expiration, AOR_DEFAULTS.minimum_expiration);
    const maxExp = withDefault(form.maximum_expiration, AOR_DEFAULTS.maximum_expiration);
    if (!e.minimum_expiration && !e.maximum_expiration && minExp > maxExp) {
      e.minimum_expiration = "Minimum Expiration cannot be greater than Maximum Expiration.";
    }

    form.contact.forEach((c, i) => {
      const v = c.trim();
      if (!v) contactErrors[i] = "Contact URI cannot be empty — remove the row or fill it in.";
      else if (v.includes(",")) contactErrors[i] = "Contact URIs are comma-separated on the server, so a single URI cannot contain a comma.";
      else if (ILLEGAL_NAME_CHARS.test(v)) contactErrors[i] = "Contact URI cannot contain [ ] ; or line breaks.";
    });
    const joinedLength = joinedContacts(form.contact).length;
    if (joinedLength > CONTACT_MAX_CHARS) {
      e.contact = `Contacts are stored as one ${CONTACT_MAX_CHARS}-character list (currently ${joinedLength}). Remove or shorten entries.`;
    }

    if ((form.outbound_proxy ?? "").length > 255) e.outbound_proxy = "Outbound Proxy must be 255 characters or fewer.";
    if ((form.mailboxes ?? "").length > 255) e.mailboxes = "Mailboxes must be 255 characters or fewer.";

    const summary = [
      ...Object.values(e),
      ...Object.values(contactErrors),
    ];
    return { fields: e, contacts: contactErrors, summary, valid: summary.length === 0 };
  }, [form, list, editing]);

  const handleSave = () => {
    setShowErrors(true);
    if (!errors.valid) return;

    const payload: AorCreate = {
      name: form.name.trim(),
      max_contacts: withDefault(form.max_contacts, AOR_DEFAULTS.max_contacts),
      minimum_expiration: withDefault(form.minimum_expiration, AOR_DEFAULTS.minimum_expiration),
      maximum_expiration: withDefault(form.maximum_expiration, AOR_DEFAULTS.maximum_expiration),
      qualify_frequency: withDefault(form.qualify_frequency, AOR_DEFAULTS.qualify_frequency),
      qualify_timeout: withDefault(form.qualify_timeout, AOR_DEFAULTS.qualify_timeout),
      qualify_2xx_only: form.qualify_2xx_only,
      authenticate_qualify: form.authenticate_qualify,
      remove_existing: form.remove_existing,
      remove_unavailable: form.remove_unavailable,
      support_path: form.support_path,
      contact: form.contact.map((c) => c.trim()).filter(Boolean),
      outbound_proxy: form.outbound_proxy?.trim() || null,
      mailboxes: form.mailboxes?.trim() || null,
    };

    if (editing) {
      updateMut.mutate({ id: editing.id, data: payload }, {
        onSuccess: () => { setOpen(false); toast.success("AOR updated"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    } else {
      createMut.mutate(payload, {
        onSuccess: () => { setOpen(false); toast.success("AOR created"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    }
  };

  const updateField = (field: keyof AorForm, value: unknown) => {
    setShowErrors(true);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addContact = () => { setShowErrors(true); setForm((prev) => ({ ...prev, contact: [...prev.contact, ""] })); };
  const removeContact = (i: number) => {
    setForm((prev) => ({ ...prev, contact: prev.contact.filter((_, idx) => idx !== i) }));
  };
  const updateContact = (i: number, val: string) => {
    setShowErrors(true);
    setForm((prev) => {
      const next = [...prev.contact];
      next[i] = val;
      return { ...prev, contact: next };
    });
  };

  const err = (key: string) => (showErrors ? errors.fields[key] : undefined);
  const joinedLength = joinedContacts(form.contact).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="AORs"
        description="Address of Record configurations"
      />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search AORs..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create AOR</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(a) => a.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No AORs configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit AOR" : "Create AOR"}
        description="An Address of Record defines where and how an endpoint can be reached"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!errors.valid}
              loading={createMut.isPending || updateMut.isPending}
            >
              {editing ? "Update" : "Create"}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          <Field
            label="Name"
            required
            htmlFor="name"
            error={err("name")}
            info="Required. Unique AOR identifier, used as the pjsip.conf section name and referenced by endpoints. Cannot contain [ ] ; or line breaks. Example: trunk-aor, phone-1001-aor."
          >
            <Input id="name" value={form.name} error={!!err("name")} maxLength={NAME_MAX_CHARS} onChange={(e) => updateField("name", e.target.value)} />
          </Field>

          <FormSection
            title="Contacts"
            columns={1}
            description="Optional. Static destinations for this AOR (pjsip.conf contact)."
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Contact SIP URIs <HelpIcon text="Optional (pjsip.conf 'contact'). Static destinations to send calls to. Each entry is a sip:user@host:port URI; multiple contacts fork in parallel (ring all at once). Leave empty for a registration-only AOR, where contacts are created dynamically by incoming REGISTER requests. An outbound trunk AOR with no contact and no registration has nowhere to send calls, so those calls fail." /></Label>
                <Button type="button" variant="ghost" size="sm" onClick={addContact}>
                  <Plus size={12} /> Add
                </Button>
              </div>
              {form.contact.length === 0 && (
                <FieldNote>
                  No static contacts. This AOR accepts contacts from REGISTER only — add one if this AOR is used for an outbound trunk.
                </FieldNote>
              )}
              <div className="space-y-1.5">
                {form.contact.map((c, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center gap-1">
                      <Input
                        value={c}
                        error={showErrors && !!errors.contacts[i]}
                        onChange={(e) => updateContact(i, e.target.value)}
                        placeholder={`sip:${form.name || "endpoint"}@host:5060`}
                        className="flex-1"
                      />
                      <Button type="button" variant="ghost" size="icon" aria-label="Remove contact" onClick={() => removeContact(i)}>
                        <X size={14} />
                      </Button>
                    </div>
                    {showErrors && errors.contacts[i] && (
                      <p role="alert" className="text-xs text-destructive-text">{errors.contacts[i]}</p>
                    )}
                  </div>
                ))}
              </div>
              {form.contact.length > 0 && (
                <FieldNote variant={joinedLength > CONTACT_MAX_CHARS ? "warning" : "muted"}>
                  {joinedLength}/{CONTACT_MAX_CHARS} characters used (all contacts are stored as one comma-separated list).
                </FieldNote>
              )}
              {err("contact") && <p role="alert" className="text-xs text-destructive-text">{err("contact")}</p>}
            </div>
          </FormSection>

          <FormSection title="Registration" columns={3} description="Optional. Leave a box empty to use the Asterisk default.">
            <Field
              label="Max Contacts"
              htmlFor="max_contacts"
              error={err("max_contacts")}
              info="Optional (pjsip.conf 'max_contacts'). Maximum number of contacts that may be bound to this AOR at once. 0 means NO contact may bind — registration is refused entirely, so never use 0 to mean 'unlimited'. Use 1 to allow a single device, or a higher number for multi-device users. Default 1."
            >
              <Input type="number" min={0} id="max_contacts" error={!!err("max_contacts")} value={form.max_contacts} placeholder={String(AOR_DEFAULTS.max_contacts)} onChange={(e) => updateField("max_contacts", numOrEmpty(e.target.value))} />
            </Field>
            <Field
              label="Minimum Expiration"
              htmlFor="minimum_expiration"
              error={err("minimum_expiration")}
              info="Optional (pjsip.conf 'minimum_expiration'). Shortest registration lifetime accepted, in seconds. Clients asking for less are rejected with 423 Interval Too Brief. Must not exceed Maximum Expiration. Default 60."
            >
              <Input type="number" min={0} id="minimum_expiration" error={!!err("minimum_expiration")} value={form.minimum_expiration} placeholder={String(AOR_DEFAULTS.minimum_expiration)} onChange={(e) => updateField("minimum_expiration", numOrEmpty(e.target.value))} />
            </Field>
            <Field
              label="Maximum Expiration"
              htmlFor="maximum_expiration"
              error={err("maximum_expiration")}
              info="Optional (pjsip.conf 'maximum_expiration'). Longest registration lifetime allowed, in seconds. Clients asking for more are capped to this value. Default 7200 (2 hours)."
            >
              <Input type="number" min={0} id="maximum_expiration" error={!!err("maximum_expiration")} value={form.maximum_expiration} placeholder={String(AOR_DEFAULTS.maximum_expiration)} onChange={(e) => updateField("maximum_expiration", numOrEmpty(e.target.value))} />
            </Field>
            {form.max_contacts === 0 && (
              <FieldNote variant="warning" className="sm:col-span-3">
                Max Contacts is 0: no device will be able to register against this AOR. Set it to 1 or more unless that is intentional.
              </FieldNote>
            )}
          </FormSection>

          <FormSection title="Qualify" columns={2} description="Optional. Reachability checks using SIP OPTIONS.">
            <Field
              label="Qualify Frequency"
              htmlFor="qualify_frequency"
              error={err("qualify_frequency")}
              info="Optional (pjsip.conf 'qualify_frequency'). How often, in seconds, to send an OPTIONS ping to each contact to check reachability. 0 disables qualifying. 60 is a common value and enables failover detection. Default 0."
            >
              <Input type="number" min={0} id="qualify_frequency" error={!!err("qualify_frequency")} value={form.qualify_frequency} placeholder={String(AOR_DEFAULTS.qualify_frequency)} onChange={(e) => updateField("qualify_frequency", numOrEmpty(e.target.value))} />
            </Field>
            <Field
              label="Qualify Timeout"
              htmlFor="qualify_timeout"
              error={err("qualify_timeout")}
              info="Optional (pjsip.conf 'qualify_timeout'). Seconds to wait for the OPTIONS response before marking the contact unreachable. 0 disables the timeout. Shorter values detect failures faster but cause false positives on high-latency links. Default 3.0."
            >
              <Input type="number" step="0.1" min={0} id="qualify_timeout" error={!!err("qualify_timeout")} value={form.qualify_timeout} placeholder={String(AOR_DEFAULTS.qualify_timeout)} onChange={(e) => updateField("qualify_timeout", numOrEmpty(e.target.value))} />
            </Field>
          </FormSection>

          <FormSection title="Routing" columns={2} description="Optional.">
            <Field
              label="Outbound Proxy"
              htmlFor="outbound_proxy"
              error={err("outbound_proxy")}
              info="Optional (pjsip.conf 'outbound_proxy'). Full SIP URI of a proxy that all requests to this AOR are routed through. Example: sip:proxy.provider.com:5060. Leave empty to send requests straight to the contact."
            >
              <Input id="outbound_proxy" value={form.outbound_proxy ?? ""} error={!!err("outbound_proxy")} onChange={(e) => updateField("outbound_proxy", e.target.value || null)} placeholder="sip:proxy.provider.com:5060" />
            </Field>
            <Field
              label="Mailboxes"
              htmlFor="mailboxes"
              error={err("mailboxes")}
              info="Optional (pjsip.conf 'mailboxes'). Comma-separated mailboxes this AOR subscribes to for MWI (Message Waiting Indicator). Format: mailbox@context. Example: 1001@default."
            >
              <Input id="mailboxes" value={form.mailboxes ?? ""} error={!!err("mailboxes")} onChange={(e) => updateField("mailboxes", e.target.value || null)} placeholder="1001@default" />
            </Field>
          </FormSection>

          <FormSection title="Flags" columns={1} description="Optional. All default to off.">
            <Switch id="qualify_2xx_only" checked={form.qualify_2xx_only} onChange={(e) => updateField("qualify_2xx_only", e.target.checked)} label={<span>Qualify 2xx Only <HelpIcon text="Optional (pjsip.conf 'qualify_2xx_only'). Off (default): any SIP response to the OPTIONS ping counts as reachable. On: only a 2xx response counts, so a contact replying 401/404/503 is treated as unreachable." /></span>} />
            <Switch id="authenticate_qualify" checked={form.authenticate_qualify} onChange={(e) => updateField("authenticate_qualify", e.target.checked)} label={<span>Authenticate Qualify <HelpIcon text="Optional (pjsip.conf 'authenticate_qualify'). Off (default): an OPTIONS challenge is accepted as proof the contact is alive. On: Asterisk answers the challenge with the endpoint's outbound auth credentials. Only enable when the remote side rejects unauthenticated OPTIONS." /></span>} />
            <Switch id="remove_existing" checked={form.remove_existing} onChange={(e) => updateField("remove_existing", e.target.checked)} label={<span>Remove Existing <HelpIcon text="Optional (pjsip.conf 'remove_existing'). Controls what happens when a new REGISTER would push the AOR past Max Contacts. Off (default): the new registration is rejected with 403. On: Asterisk removes the oldest contact(s) to make room and accepts the new one. It does not wipe the contact list on every REGISTER — to allow only one device at a time, set Max Contacts to 1." /></span>} />
            <Switch id="remove_unavailable" checked={form.remove_unavailable} onChange={(e) => updateField("remove_unavailable", e.target.checked)} label={<span>Remove Unavailable <HelpIcon text="Optional (pjsip.conf 'remove_unavailable'). When Max Contacts is reached, prefer removing contacts that failed qualify before removing the oldest one. Requires Qualify Frequency to be non-zero to have any effect." /></span>} />
            <Switch id="support_path" checked={form.support_path} onChange={(e) => updateField("support_path", e.target.checked)} label={<span>Support Path <HelpIcon text="Optional (pjsip.conf 'support_path'). Store the Path header from REGISTER (RFC 3327) and use it when routing calls back to the contact. Enable when the device registers through an edge proxy or SBC." /></span>} />
          </FormSection>

          <ValidationSummary errors={errors.summary} />
        </div>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete AOR"
        description={<>Delete <strong>{pendingDelete?.name}</strong>? This cannot be undone.</>}
        loading={deleteMut.isPending}
        onConfirm={() =>
          pendingDelete &&
          deleteMut.mutate(pendingDelete.id, {
            onSuccess: () => { toast.success("AOR deleted"); setPendingDelete(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
